import { useApi } from "@/hooks/useApi";
import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
} from "@tanstack/react-query";

type HttpMethod = "post" | "put" | "patch" | "delete";

type UseApiMutationOptions<TInput, TOutput, TError = Error> = Omit<
  UseMutationOptions<TOutput, TError, TInput>,
  "mutationFn"
> & {
  path: string | ((variables: TInput) => string);
  method?: HttpMethod;
};

/**
 * Standard mutation wrapper for POST/PUT/PATCH/DELETE calls on the shaarp
 * API. The path can be a static string or computed from the input variables
 * (for /:id endpoints). Query invalidation should be wired in the caller's
 * `onSuccess` via the queryClient.
 */
export function useApiMutation<TInput = void, TOutput = void, TError = Error>({
  path,
  method = "post",
  ...options
}: UseApiMutationOptions<TInput, TOutput, TError>): UseMutationResult<
  TOutput,
  TError,
  TInput
> {
  const api = useApi();

  return useMutation<TOutput, TError, TInput>({
    mutationFn: async (variables) => {
      const resolvedPath =
        typeof path === "function" ? path(variables) : path;
      if (method === "delete") {
        const { data } = await api.delete<TOutput>(resolvedPath, {
          data: variables,
        });
        return data;
      }
      const { data } = await api[method]<TOutput>(resolvedPath, variables);
      return data;
    },
    ...options,
  });
}
